// Horario de trabajo del usuario logueado, para que los 4 calendarios (día, semana, mes y el
// selector de NuevoTurno) dibujen el MISMO rango sin repetir el `for (let h = 9; h <= 22)` que
// antes estaba escrito a mano en cada uno.
import { computed } from "vue";
import { useUsuariosStore } from "@/stores/usuarios";
import {
  generarSlots,
  fueraDeHorario,
  normalizarDiasActivos,
  claveDia,
  APERTURA_DEFECTO,
  CIERRE_DEFECTO,
} from "@/utils/horarios";

export function useHorarioAgenda() {
  const usuariosStore = useUsuariosStore();

  // Los campos horarioApertura/horarioCierre existen en Firestore desde abril de 2026, pero la
  // pantalla para elegirlos nunca se construyó: el formulario de Ajustes los guardaba igual, con
  // sus valores por defecto, cada vez que alguien tocaba "Guardar cambios". Por eso NO alcanza
  // con que el campo exista — solo se respeta si `horarioConfigurado` dice que el usuario lo
  // eligió a propósito. Sin esa guarda, esos valores fantasma le cambiarían la agenda a gente
  // que nunca pidió nada.
  const configurado = computed(
    () => usuariosStore.usuarioActual?.horarioConfigurado === true,
  );

  const apertura = computed(() =>
    configurado.value
      ? usuariosStore.usuarioActual?.horarioApertura || APERTURA_DEFECTO
      : APERTURA_DEFECTO,
  );

  const cierre = computed(() =>
    configurado.value
      ? usuariosStore.usuarioActual?.horarioCierre || CIERRE_DEFECTO
      : CIERRE_DEFECTO,
  );

  // `turnos` deben ser solo los del período que se dibuja (ver generarSlots).
  function slotsPara(turnos = []) {
    return generarSlots(apertura.value, cierre.value, turnos);
  }

  function estaFueraDeHorario(hora) {
    return fueraDeHorario(hora, apertura.value, cierre.value);
  }

  // Días que el usuario atiende. Acá no hace falta la guarda de `horarioConfigurado`: lo que se
  // venía guardando solo desde abril eran los 7 días en true, que es exactamente el
  // comportamiento de siempre.
  const diasActivos = computed(() =>
    normalizarDiasActivos(usuariosStore.usuarioActual?.diasActivos),
  );

  // `fecha` puede ser "YYYY-MM-DD" o un Date.
  function esDiaActivo(fecha) {
    const clave = claveDia(fecha);
    return clave ? diasActivos.value[clave] === true : true;
  }

  return { apertura, cierre, slotsPara, estaFueraDeHorario, diasActivos, esDiaActivo };
}
